import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { PrimaryOffer } from 'components/Offer/Primary';
import { SecondaryOfferList } from 'components/OfferList/SecondaryOfferList';
import { MoreDealsButtonVertical } from 'components/OfferList/SecondaryOfferList/MoreDealsButton/MoreDealsButtonVertical';
import { PriceWatchCTAWrapper } from 'components/PriceWatchCTAWrapper/PriceWatchCTAWrapper';
import { getSecondaryOffers } from 'components/SplitBookings/helpers';
import { useOfferContainer } from 'hooks/useOfferContainer';
import { getDeviceLayout } from 'modules/meta/selectors';
import { getHotel, getIsAnchorHotel, getIsSearchCompleted, getSplitBookingDetails } from 'modules/sapiSearch/selectors';
import { HotelOfferEntity } from 'modules/sapiSearch/slice';
import { cssTheme } from '@daedalus/atlas/themes';
import { Page } from '@daedalus/core/src/analytics/types/Events';
import { OfferPrimaryLayoutVariant } from '@daedalus/core/src/offer/types/OfferLayout';
import { OfferVariant } from '@daedalus/core/src/offer/types/OfferVariant';
import { hexToRGBAString } from '@daedalus/core/src/utils/css';
import { PriceWatchDetailedToggle } from '@daedalus/shared/src/priceWatch/components/PriceWatchDetailedToggle';
const _exp2 = /*#__PURE__*/() => ({
  isSplitBooking = false
}) => isSplitBooking ? cssTheme.colors.border.neutral.c200 : cssTheme.colors.border.neutral.c100;
const Wrapper = /*#__PURE__*/styled('div')({
  name: "Wrapper",
  class: "w97dsp2",
  propsAsIs: false,
  vars: {
    "w97dsp2-0": [_exp2()]
  }
});
const _exp3 = /*#__PURE__*/() => ({
  isPrimaryOfferMemberDeal = false
}) => isPrimaryOfferMemberDeal ? cssTheme.colors.border.special.c200 : cssTheme.colors.border.interactive.c200;
const _exp4 = /*#__PURE__*/() => ({
  variant
}) => variant === OfferVariant.SplitBooking ? cssTheme.colors.background.brand.c050 : hexToRGBAString(cssTheme.colors.background.brand.c050, cssTheme.opacity.o50);
const _exp10 = /*#__PURE__*/() => ({
  hasMargin = true
}) => hasMargin ? cssTheme.layout.spacing.s100 : 0;
const _exp11 = /*#__PURE__*/() => ({
  variant,
  hasSecondaryOffers = true
}) => variant !== OfferVariant.SplitBooking && hasSecondaryOffers ? '-1px -1px 0 -1px' : '0';
const PrimaryWrapper = /*#__PURE__*/styled('section')({
  name: "PrimaryWrapper",
  class: "p10jsleg",
  propsAsIs: false,
  vars: {
    "p10jsleg-0": [_exp3()],
    "p10jsleg-1": [_exp4()],
    "p10jsleg-2": [_exp10()],
    "p10jsleg-3": [_exp11()]
  }
});
const SecondaryWrapper = /*#__PURE__*/styled('section')({
  name: "SecondaryWrapper",
  class: "ss9hvnd",
  propsAsIs: false
});
interface MainProps {
  isLoading?: boolean;
  offerEntity?: HotelOfferEntity;
  component?: string;
  primaryOfferVariant: OfferPrimaryLayoutVariant;
  onSeeMoreClick?: () => void;
  primaryOfferRef?: React.Ref<HTMLElement>;
  /** Allows to pass a component that will be rendered as unavailability component */
  unavailabilitySlot?: React.ReactNode;
  /** Support for OTA Mode */
  isOTAMode?: boolean;
  primaryOfferShouldShowTaxation?: boolean;
  withHotelCard?: boolean;
  shouldShowCtaLockIcon?: boolean;
  isPrimaryOfferMemberDeal?: boolean;
  shouldRenderUnavailabilitySlot?: boolean;
  page?: Page;
}
interface LoadingProps {
  isLoading: true;
  primaryOfferVariant: OfferPrimaryLayoutVariant;
  offerEntity?: never;
  component?: never;
  onSeeMoreClick?: never;
  primaryOfferRef?: never;
  unavailabilitySlot?: never;
  isOTAMode?: never;
  primaryOfferShouldShowTaxation?: never;
  withHotelCard?: never;
  shouldShowCtaLockIcon?: never;
  isPrimaryOfferMemberDeal?: never;
  shouldRenderUnavailabilitySlot?: never;
  isAnchor?: boolean;
  page?: Page;
}
const OfferBoxComponent: React.FC<MainProps | LoadingProps> = ({
  offerEntity,
  isLoading,
  component,
  primaryOfferVariant,
  onSeeMoreClick,
  primaryOfferRef,
  unavailabilitySlot = <></>,
  isOTAMode = false,
  primaryOfferShouldShowTaxation = true,
  withHotelCard = false,
  shouldShowCtaLockIcon = true,
  isPrimaryOfferMemberDeal = false,
  shouldRenderUnavailabilitySlot = false,
  page = Page.Search
}) => {
  const {
    id: hotelId = ''
  } = offerEntity || {};
  const isAnchor = useSelector(state => getIsAnchorHotel(state, hotelId));
  const hotel = useSelector(state => getHotel(state, hotelId));
  const anchorComplete = useSelector(getIsSearchCompleted);
  const shouldShowPriceWatchToggle = page === Page.Search && isAnchor && anchorComplete;
  const {
    topOffers,
    offerVariant,
    anchorPrice,
    anchorPriceTotal
  } = useOfferContainer({
    offerEntity,
    hotelId,
    isOTAMode
  });
  const availableOffersCount = isOTAMode ? 1 : offerEntity?.availableOffersCount || 1;
  const {
    splitBookingOffers,
    showSplitBookingLoader,
    hasSplitBookingOffer,
    splitBookingType,
    splitBookingPosition,
    offerPositionToRemove,
    splitBookingBundle
  } = useSelector(state => getSplitBookingDetails(state, hotelId));
  const deviceLayout = useSelector(getDeviceLayout);
  const renderSplitBookingPrimaryOffer = useCallback(hasSecondaryOffers => {
    return <PrimaryWrapper ref={primaryOfferRef} variant={splitBookingType === 'split_flow' ? OfferVariant.SplitBooking : OfferVariant.Popular} hasMargin={false} className={cx('__hover', withHotelCard && '__withHotelCard')} hasSecondaryOffers={hasSecondaryOffers}>
          <PrimaryOffer isSplitBooking anchorPrice={anchorPrice} anchorPriceTotal={anchorPriceTotal} hotelId={hotelId} shouldShowTaxation={primaryOfferShouldShowTaxation} shouldShowTaxationAndNightly variant={primaryOfferVariant} splitBookingOffers={splitBookingOffers} splitBookingBundle={splitBookingBundle} splitBookingType={splitBookingType} sourceComponent={component} />
        </PrimaryWrapper>;
  }, [anchorPrice, anchorPriceTotal, component, hotelId, primaryOfferRef, primaryOfferShouldShowTaxation, primaryOfferVariant, splitBookingOffers, splitBookingBundle, splitBookingType, withHotelCard]);
  const primaryOffer = topOffers?.[0];
  const hasSingleSBOffer = hasSplitBookingOffer && splitBookingType === 'single_flow';
  const hasSplitSBOffer = hasSplitBookingOffer && splitBookingType === 'split_flow';
  const hasPrimarySBOffer = hasSingleSBOffer && splitBookingPosition === 0;
  const secondaryOffers = useMemo(() => {
    const offers = getSecondaryOffers({
      topOffers,
      hasSplitBookingOffer: hasSingleSBOffer,
      splitBookingType,
      splitBookingPosition,
      offerPositionToRemove,
      showSplitBookingLoader,
      deviceLayout
    });
    return offers;
  }, [topOffers, hasSingleSBOffer, splitBookingType, splitBookingPosition, offerPositionToRemove, showSplitBookingLoader, deviceLayout]);
  const viewMoreDealsButton = useMemo(() => {
    if (!onSeeMoreClick || availableOffersCount < 3) return;
    return <MoreDealsButtonVertical onClick={onSeeMoreClick} />;
  }, [availableOffersCount, onSeeMoreClick]);
  if (!topOffers.length) {
    if (!isLoading && !showSplitBookingLoader) {
      if (hasSingleSBOffer || hasSplitSBOffer) {
        return <Wrapper isSplitBooking className={cx(!isOTAMode && topOffers.length > 1 && '__border', withHotelCard && '__withHotelCard')}>
            {renderSplitBookingPrimaryOffer(false)}
          </Wrapper>;
      }
      if (shouldRenderUnavailabilitySlot) {
        return <>{unavailabilitySlot}</>;
      }
    }
    return <Wrapper className={cx('__border', withHotelCard && '__withHotelCard')}>
        <PrimaryWrapper data-id="loader" variant={OfferVariant.Loading} ref={primaryOfferRef} className={cx(withHotelCard && '__withHotelCard')} isPrimaryOfferMemberDeal={isPrimaryOfferMemberDeal}>
          <PrimaryOffer isLoading variant={primaryOfferVariant} />
        </PrimaryWrapper>
        <SecondaryOfferList isLoading variant="vertical" nLoaders={onSeeMoreClick ? 3 : 2} />
      </Wrapper>;
  }
  return <Wrapper className={cx(!isOTAMode && topOffers.length > 1 && '__border', withHotelCard && '__withHotelCard')}>
      {hasPrimarySBOffer ? renderSplitBookingPrimaryOffer(true) : <PrimaryWrapper ref={primaryOfferRef} variant={offerVariant} hasMargin={!isOTAMode} className={cx(withHotelCard && '__withHotelCard')} isPrimaryOfferMemberDeal={isPrimaryOfferMemberDeal}>
          <PrimaryOffer anchorPrice={anchorPrice} anchorPriceTotal={anchorPriceTotal} hotelId={hotelId} offer={primaryOffer} offerVariant={offerVariant} shouldShowTaxation={primaryOfferShouldShowTaxation} shouldShowTaxationAndNightly variant={primaryOfferVariant} shouldShowCtaLockIcon={shouldShowCtaLockIcon} hasInteraction sourceComponent={component} />
        </PrimaryWrapper>}
      {!isOTAMode && <SecondaryWrapper>
          <SecondaryOfferList variant="vertical" anchorPrice={anchorPrice} anchorPriceTotal={anchorPriceTotal} splitBookingPosition={splitBookingPosition} splitBookingType={splitBookingType} splitBookingBundle={splitBookingBundle} hotelId={hotelId} secondaryOffers={secondaryOffers} sourceComponent={component} splitBookingOffers={splitBookingOffers} showSplitBookingLoader={showSplitBookingLoader} viewMoreSlot={viewMoreDealsButton} />
        </SecondaryWrapper>}

      {shouldShowPriceWatchToggle && <PriceWatchCTAWrapper hotel={hotel} ctaComponent={PriceWatchDetailedToggle} componentName="PriceWatchDetailedToggle" holdOutElement={null} searchIsCompleted />}
    </Wrapper>;
};
export const OfferBox = React.memo(OfferBoxComponent);

require("./OfferBox.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./OfferBox.tsx");